import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdAttachFile,
    MdClose,
    MdDelete,
    MdForum,
    MdPerson,
    MdRefresh,
    MdSend,
    MdSmartToy,
    MdSupportAgent,
} from 'react-icons/md'

import ConfirmModal from '@/components/confirm-modal'
import { getDepartmentsByOrganization } from '@/lib/api/atendimento/departments'
import { uploadMedia } from '@/lib/api/atendimento/chat'
import {
    createSimulatedContacts,
    deleteSimulatedContact,
    getSimulatedContacts,
    getSimulatedMessages,
    sendSimulatedMessage,
    SimulatedContact,
    SimulatedMessage,
} from '@/lib/api/atendimento/conversation-simulator'
import type { Department } from '@/lib/api/common/interfaces/department.interface'
import { getPipelines, SalesPipeline } from '@/lib/api/sales/pipelines-api'
import { getWallets, SalesWallet } from '@/lib/api/sales/wallets-api'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useResourceVisibility } from '@/lib/hooks/use-entity-access'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { SocketContext } from '@/lib/providers/socket-provider'
import { useAuth } from '@/store/hooks'

/** Mesmo marcador das mensagens do atendimento: `[Arquivo enviado: <url>]`. */
const FILE_MARKER_REGEX = /\[Arquivo enviado:\s*(https?:\/\/[^\s\]]+)\]/g
const IMAGE_URL_REGEX = /\.(jpe?g|png|gif|webp)(\?|$)/i

interface ContactForm {
    name: string
    quantity: number
    phoneNumber: string
    departmentId: string
    walletId: string
    pipelineId: string
    stageId: string
}

const EMPTY_FORM: ContactForm = {
    name: '',
    quantity: 1,
    phoneNumber: '',
    departmentId: '',
    walletId: '',
    pipelineId: '',
    stageId: '',
}

function splitContent(content: string): { text: string; files: string[] } {
    const files = [...content.matchAll(FILE_MARKER_REGEX)].map(match => match[1])
    return { text: content.replace(FILE_MARKER_REGEX, '').trim(), files }
}

function formatTime(value: string): string {
    return new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

/**
 * Simulador de conversas: o lado do CONTATO de uma conversa no canal Dex. O que ele
 * manda entra no atendimento real (fila, carteira, funil, IA); o que a equipe ou a IA
 * responde chega aqui pelo socket. Ver docs/dex-talk-channel.md.
 */
export default function ConversationSimulatorPage() {
    const { user } = useAuth()
    const { socket, isConnected } = useContext(SocketContext)
    const { ClientLabel } = useClientLabel()
    const { canCreate, canDelete } = useResourcePermissions('atendimento', 'conversation_simulator')
    const isResourceVisible = useResourceVisibility()
    const canSeeWallets = isResourceVisible('sales', 'wallets')
    const canSeePipelines = isResourceVisible('sales', 'pipelines')
    const organizationId = user?.organizationId ?? user?.company?.organizationId

    const [contacts, setContacts] = useState<SimulatedContact[]>([])
    const [loadingContacts, setLoadingContacts] = useState(true)
    const [selectedChatId, setSelectedChatId] = useState<number | null>(null)
    const [messages, setMessages] = useState<SimulatedMessage[]>([])
    const [loadingMessages, setLoadingMessages] = useState(false)
    const [draft, setDraft] = useState('')
    const [pendingFiles, setPendingFiles] = useState<File[]>([])
    const [sending, setSending] = useState(false)

    const [showForm, setShowForm] = useState(false)
    const [form, setForm] = useState<ContactForm>(EMPTY_FORM)
    const [creating, setCreating] = useState(false)
    const [departments, setDepartments] = useState<Department[]>([])
    const [wallets, setWallets] = useState<SalesWallet[]>([])
    const [pipelines, setPipelines] = useState<SalesPipeline[]>([])

    const [contactToDelete, setContactToDelete] = useState<SimulatedContact | null>(null)
    const [deleting, setDeleting] = useState(false)

    const messagesEndRef = useRef<HTMLDivElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const selectedContact = useMemo(
        () => contacts.find(contact => contact.chatId === selectedChatId) ?? null,
        [contacts, selectedChatId]
    )
    const stages = useMemo(
        () => pipelines.find(pipeline => String(pipeline.id) === form.pipelineId)?.stages ?? [],
        [pipelines, form.pipelineId]
    )

    const loadContacts = useCallback(async () => {
        const response = await getSimulatedContacts()
        if (response.success) {
            setContacts(response.result ?? [])
        } else {
            toast.error(response.error || 'Não foi possível carregar os contatos simulados')
        }
        setLoadingContacts(false)
    }, [])

    const loadMessages = useCallback(async (chatId: number) => {
        const response = await getSimulatedMessages(chatId)
        if (response.success) setMessages(response.result ?? [])
    }, [])

    useEffect(() => {
        loadContacts()
    }, [loadContacts])

    useEffect(() => {
        if (!showForm || !organizationId) return
        getDepartmentsByOrganization(organizationId, { limit: 100 }).then(response => {
            if (response.success) setDepartments(response.result ?? [])
        })
        if (canSeeWallets) {
            getWallets().then(response => {
                if (response.success) setWallets(response.result ?? [])
            })
        }
        if (canSeePipelines) {
            getPipelines().then(response => {
                if (response.success) setPipelines(response.result ?? [])
            })
        }
    }, [showForm, organizationId, canSeeWallets, canSeePipelines])

    useEffect(() => {
        if (!selectedChatId) {
            setMessages([])
            return
        }
        setLoadingMessages(true)
        loadMessages(selectedChatId).finally(() => setLoadingMessages(false))
    }, [selectedChatId, loadMessages])

    // Resposta da equipe ou da IA, entregue pela sala da conversa.
    useEffect(() => {
        if (!socket || !isConnected || !selectedChatId) return
        const chatId = selectedChatId
        socket.emit('dex-talk:join', { chatId })

        const handleMessage = (payload: { chatId: number; message: SimulatedMessage }) => {
            if (payload?.chatId !== chatId) return
            if (payload.message.id === 0) {
                // Despedida do encerramento: não é gravada na conversa, só entregue.
                setMessages(current => [...current, { ...payload.message, id: -Date.now() }])
                return
            }
            loadMessages(chatId)
        }
        socket.on('dex-talk:message', handleMessage)

        return () => {
            socket.emit('dex-talk:leave', { chatId })
            socket.off('dex-talk:message', handleMessage)
        }
    }, [socket, isConnected, selectedChatId, loadMessages])

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages])

    const updateForm = (patch: Partial<ContactForm>) => setForm(current => ({ ...current, ...patch }))

    const handleCreate = async (event: React.FormEvent) => {
        event.preventDefault()
        if (!form.name.trim() || !form.departmentId) {
            toast.error('Informe o nome e o setor')
            return
        }
        if (form.pipelineId && !form.stageId) {
            toast.error('Escolha a etapa do funil')
            return
        }
        setCreating(true)
        const response = await createSimulatedContacts({
            name: form.name.trim(),
            quantity: form.quantity,
            phoneNumber: form.phoneNumber.trim() || undefined,
            departmentId: Number(form.departmentId),
            walletId: form.walletId ? Number(form.walletId) : undefined,
            pipelineId: form.pipelineId ? Number(form.pipelineId) : undefined,
            stageId: form.stageId ? Number(form.stageId) : undefined,
        })
        setCreating(false)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível criar os contatos')
            return
        }
        const created = response.result?.created ?? []
        toast.success(created.length === 1 ? 'Contato criado' : `${created.length} contatos criados`)
        setForm(current => ({ ...EMPTY_FORM, departmentId: current.departmentId }))
        setShowForm(false)
        await loadContacts()
        if (created[0]) setSelectedChatId(created[0].chatId)
    }

    const handleSend = async (event: React.FormEvent) => {
        event.preventDefault()
        if (!selectedChatId || sending) return
        const text = draft.trim()
        if (!text && pendingFiles.length === 0) return

        setSending(true)
        let mediaUrls: string[] = []
        if (pendingFiles.length > 0) {
            const upload = await uploadMedia(pendingFiles)
            if (!upload.success) {
                setSending(false)
                toast.error(upload.error || 'Não foi possível enviar o arquivo')
                return
            }
            mediaUrls = (upload.result?.files ?? []).map(file => file.url)
        }

        const response = await sendSimulatedMessage(selectedChatId, { text: text || undefined, mediaUrls })
        setSending(false)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível enviar a mensagem')
            return
        }

        // A mensagem passa pela fila de entrada antes de existir na conversa: aparece já
        // como enviada e é trocada pela gravada na próxima leitura.
        const content = [text, ...mediaUrls.map(url => `[Arquivo enviado: ${url}]`)].filter(Boolean).join('\n')
        setMessages(current => [
            ...current,
            { id: -Date.now(), content, role: 'user', name: null, createdAt: new Date().toISOString() },
        ])
        setDraft('')
        setPendingFiles([])
        const chatId = selectedChatId
        setTimeout(() => {
            loadMessages(chatId)
            loadContacts()
        }, 2500)
    }

    const handleDelete = async () => {
        if (!contactToDelete) return
        setDeleting(true)
        const response = await deleteSimulatedContact(contactToDelete.chatId)
        setDeleting(false)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível excluir o contato')
            return
        }
        if (response.result?.failures?.length) {
            toast.warning(`Contato excluído, exceto: ${response.result.failures.join(', ')}`)
        } else {
            toast.success('Contato excluído')
        }
        if (selectedChatId === contactToDelete.chatId) setSelectedChatId(null)
        setContactToDelete(null)
        await loadContacts()
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg">
                        <MdForum className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">Simulador de conversas</h1>
                        <p className="text-sm text-base-content/60">
                            Fale como o contato pelo canal Dex. As mensagens entram no atendimento real.
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button type="button" className="btn btn-primary gap-2" onClick={() => setShowForm(open => !open)}>
                        {showForm ? <MdClose className="text-xl" /> : <MdAdd className="text-xl" />}
                        <span className="hidden sm:inline">{showForm ? 'Fechar' : 'Novo contato'}</span>
                    </button>
                )}
            </div>

            {showForm && (
                <form
                    onSubmit={handleCreate}
                    className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-5 flex flex-col gap-5"
                >
                    <div>
                        <h2 className="font-semibold text-base-content">Novo contato simulado</h2>
                        <p className="text-sm text-base-content/60 mt-1">
                            Cria o cadastro de {ClientLabel.toLowerCase()}, o lead e, com funil escolhido, a
                            oportunidade. O que o teste gerar (cobranças, agendamentos) é seu para excluir depois.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <label className="flex flex-col gap-1.5">
                            <span className="text-sm font-medium">
                                Nome <span className="text-error">*</span>
                            </span>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex: Contato de teste"
                                value={form.name}
                                onChange={event => updateForm({ name: event.target.value })}
                                maxLength={80}
                            />
                        </label>
                        <label className="flex flex-col gap-1.5">
                            <span className="text-sm font-medium">Quantidade</span>
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                min={1}
                                max={50}
                                value={form.quantity}
                                onChange={event =>
                                    updateForm({ quantity: Math.min(50, Math.max(1, Number(event.target.value) || 1)) })
                                }
                            />
                            <span className="text-xs text-base-content/50">Com mais de um, cada nome ganha um número.</span>
                        </label>
                        <label className="flex flex-col gap-1.5">
                            <span className="text-sm font-medium">Telefone</span>
                            <input
                                type="tel"
                                className="input input-bordered w-full"
                                placeholder="Opcional"
                                value={form.phoneNumber}
                                onChange={event => updateForm({ phoneNumber: event.target.value })}
                            />
                            <span className="text-xs text-base-content/50">Sem telefone, a IA pede antes de agendar.</span>
                        </label>
                        <label className="flex flex-col gap-1.5">
                            <span className="text-sm font-medium">
                                Setor <span className="text-error">*</span>
                            </span>
                            <select
                                className="select select-bordered w-full"
                                value={form.departmentId}
                                onChange={event => updateForm({ departmentId: event.target.value })}
                            >
                                <option value="">Selecione</option>
                                {departments.map(department => (
                                    <option key={department.id} value={department.id}>
                                        {department.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                        {canSeeWallets && (
                            <label className="flex flex-col gap-1.5">
                                <span className="text-sm font-medium">Carteira</span>
                                <select
                                    className="select select-bordered w-full"
                                    value={form.walletId}
                                    onChange={event => updateForm({ walletId: event.target.value })}
                                >
                                    <option value="">Rodízio da organização</option>
                                    {wallets.map(wallet => (
                                        <option key={wallet.id} value={wallet.id}>
                                            {wallet.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                        {canSeePipelines && (
                            <>
                                <label className="flex flex-col gap-1.5">
                                    <span className="text-sm font-medium">Funil</span>
                                    <select
                                        className="select select-bordered w-full"
                                        value={form.pipelineId}
                                        onChange={event => updateForm({ pipelineId: event.target.value, stageId: '' })}
                                    >
                                        <option value="">Sem oportunidade</option>
                                        {pipelines.map(pipeline => (
                                            <option key={pipeline.id} value={pipeline.id}>
                                                {pipeline.name}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                {form.pipelineId && (
                                    <label className="flex flex-col gap-1.5">
                                        <span className="text-sm font-medium">
                                            Etapa <span className="text-error">*</span>
                                        </span>
                                        <select
                                            className="select select-bordered w-full"
                                            value={form.stageId}
                                            onChange={event => updateForm({ stageId: event.target.value })}
                                        >
                                            <option value="">Selecione</option>
                                            {stages.map(stage => (
                                                <option key={stage.id} value={stage.id}>
                                                    {stage.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                            </>
                        )}
                    </div>

                    <div className="flex justify-end gap-2">
                        <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>
                            Cancelar
                        </button>
                        <button type="submit" className="btn btn-primary gap-2" disabled={creating}>
                            {creating ? <span className="loading loading-spinner loading-sm" /> : <MdAdd className="text-lg" />}
                            Criar
                        </button>
                    </div>
                </form>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm flex flex-col min-h-[240px] lg:h-[calc(100vh-260px)]">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-base-300/50">
                        <span className="font-semibold text-sm">Contatos ({contacts.length})</span>
                        <button type="button" className="btn btn-ghost btn-xs" onClick={loadContacts} title="Atualizar">
                            <MdRefresh className="text-lg" />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {loadingContacts ? (
                            <div className="flex justify-center py-10">
                                <span className="loading loading-spinner text-primary" />
                            </div>
                        ) : contacts.length === 0 ? (
                            <p className="text-sm text-base-content/60 text-center px-6 py-10">
                                Nenhum contato simulado. Crie um em "Novo contato".
                            </p>
                        ) : (
                            <ul className="divide-y divide-base-300/50">
                                {contacts.map(contact => (
                                    <li key={contact.chatId}>
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => setSelectedChatId(contact.chatId)}
                                            onKeyDown={event => event.key === 'Enter' && setSelectedChatId(contact.chatId)}
                                            className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-base-200/60 ${
                                                selectedChatId === contact.chatId ? 'bg-primary/10' : ''
                                            }`}
                                        >
                                            <div className="w-9 h-9 shrink-0 rounded-full bg-base-300 flex items-center justify-center">
                                                <MdPerson className="text-lg text-base-content/60" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium truncate">{contact.name}</p>
                                                <p className="text-xs text-base-content/50 truncate">
                                                    {contact.department?.name ?? 'Sem setor'} ·{' '}
                                                    {contact.status === 'OPEN' ? 'Em atendimento' : 'Encerrada'}
                                                </p>
                                            </div>
                                            {canDelete && (
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-xs text-error"
                                                    title="Excluir contato"
                                                    onClick={event => {
                                                        event.stopPropagation()
                                                        setContactToDelete(contact)
                                                    }}
                                                >
                                                    <MdDelete className="text-base" />
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm flex flex-col h-[70vh] lg:h-[calc(100vh-260px)]">
                    {!selectedContact ? (
                        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-base-content/50 px-6 text-center">
                            <MdForum className="text-5xl" />
                            <p className="text-sm">Escolha um contato para conversar como ele.</p>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-3 px-4 py-3 border-b border-base-300/50">
                                <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
                                    <MdPerson className="text-lg text-primary" />
                                </div>
                                <div className="min-w-0">
                                    <p className="font-semibold text-sm truncate">{selectedContact.name}</p>
                                    <p className="text-xs text-base-content/50">
                                        Você está falando como o contato · {isConnected ? 'conectado' : 'reconectando...'}
                                    </p>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto bg-base-200/40 px-4 py-4 flex flex-col gap-2">
                                {loadingMessages ? (
                                    <div className="flex justify-center py-10">
                                        <span className="loading loading-spinner text-primary" />
                                    </div>
                                ) : messages.length === 0 ? (
                                    <p className="text-sm text-base-content/50 text-center py-10">
                                        Mande a primeira mensagem para abrir o atendimento.
                                    </p>
                                ) : (
                                    messages.map(message => <MessageBubble key={message.id} message={message} />)
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {canCreate && (
                                <form onSubmit={handleSend} className="border-t border-base-300/50 p-3 flex flex-col gap-2">
                                    {pendingFiles.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {pendingFiles.map((file, index) => (
                                                <span key={`${file.name}-${index}`} className="badge badge-outline gap-1 py-3">
                                                    {file.name}
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setPendingFiles(current => current.filter((_, i) => i !== index))
                                                        }
                                                    >
                                                        <MdClose />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="flex items-end gap-2">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*,application/pdf"
                                            multiple
                                            className="hidden"
                                            onChange={event => {
                                                const files = Array.from(event.target.files ?? [])
                                                setPendingFiles(current => [...current, ...files].slice(0, 10))
                                                event.target.value = ''
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-square"
                                            title="Anexar arquivo"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <MdAttachFile className="text-xl" />
                                        </button>
                                        <textarea
                                            className="textarea textarea-bordered flex-1 min-h-[44px] max-h-32 resize-none"
                                            rows={1}
                                            placeholder="Escreva como o contato..."
                                            value={draft}
                                            onChange={event => setDraft(event.target.value)}
                                            onKeyDown={event => {
                                                if (event.key === 'Enter' && !event.shiftKey) {
                                                    event.preventDefault()
                                                    handleSend(event)
                                                }
                                            }}
                                        />
                                        <button
                                            type="submit"
                                            className="btn btn-primary btn-square"
                                            disabled={sending || (!draft.trim() && pendingFiles.length === 0)}
                                            title="Enviar"
                                        >
                                            {sending ? (
                                                <span className="loading loading-spinner loading-sm" />
                                            ) : (
                                                <MdSend className="text-xl" />
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </>
                    )}
                </div>
            </div>

            <ConfirmModal
                isOpen={!!contactToDelete}
                onClose={() => setContactToDelete(null)}
                onConfirm={handleDelete}
                title="Excluir contato simulado"
                message={`Exclui a conversa, o lead, o cadastro e a oportunidade criados para "${
                    contactToDelete?.name ?? ''
                }". Cobranças, agendamentos e o que mais o teste gerou continuam e precisam ser excluídos à mão.`}
                confirmButtonText="Excluir"
                confirmButtonClass="btn-error"
                loading={deleting}
            />
        </div>
    )
}

function MessageBubble({ message }: { message: SimulatedMessage }) {
    const isContact = message.role === 'user'
    const isSystem = message.role === 'system'
    const { text, files } = splitContent(message.content)

    if (isSystem) {
        return (
            <div className="self-center max-w-[85%] text-center text-xs text-base-content/60 bg-base-100 rounded-lg px-3 py-2 border border-base-300/50">
                {text}
            </div>
        )
    }

    const AuthorIcon = message.role === 'assistant' ? MdSmartToy : MdSupportAgent
    return (
        <div className={`flex flex-col max-w-[80%] ${isContact ? 'self-end items-end' : 'self-start items-start'}`}>
            {!isContact && (
                <span className="flex items-center gap-1 text-[11px] text-base-content/50 mb-0.5 px-1">
                    <AuthorIcon />
                    {message.role === 'assistant' ? 'IA' : message.name || 'Equipe'}
                </span>
            )}
            <div
                className={`rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words shadow-sm ${
                    isContact ? 'bg-primary text-primary-content rounded-br-sm' : 'bg-base-100 rounded-bl-sm'
                }`}
            >
                {files.map(url =>
                    IMAGE_URL_REGEX.test(url) ? (
                        <a key={url} href={url} target="_blank" rel="noreferrer">
                            <img src={url} alt="" className="rounded-lg max-h-56 mb-1" />
                        </a>
                    ) : (
                        <a key={url} href={url} target="_blank" rel="noreferrer" className="underline block mb-1">
                            Arquivo
                        </a>
                    )
                )}
                {text}
                <span className={`block text-[10px] mt-1 text-right ${isContact ? 'opacity-70' : 'text-base-content/40'}`}>
                    {message.id < 0 && isContact ? 'enviando · ' : ''}
                    {formatTime(message.createdAt)}
                </span>
            </div>
        </div>
    )
}
